import { QueryClientProvider } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { AuthProvider } from './AuthProvider';
import { ThemeProvider } from './ThemeProvider';
import { ToastProvider } from './ToastProvider';

/**
 * Provider composition, ordered outermost first.
 *
 * ErrorBoundary wraps everything so a provider that throws still renders a recovery
 * screen. ToastProvider sits inside QueryClientProvider so query error handlers can
 * raise toasts. AuthProvider is innermost: it needs both the query client (to clear
 * caches on sign-out) and the router (its guards redirect), so main.tsx nests the
 * router outside this.
 */
export function AppProviders({
  queryClient,
  children,
}: {
  readonly queryClient: QueryClient;
  readonly children: ReactNode;
}) {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <AuthProvider>{children}</AuthProvider>
          </ToastProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
